import React, { useEffect, useMemo, useState } from "react";
import FilterDimension from "../../components/FilterDimension";
import { getAllChargingSites } from "../../api/ChargingSites";
import { useAuth } from "../../providers/AuthContext";
import toast from 'react-hot-toast';
import { useTranslation } from "react-i18next";


const COOKIE_KEY = "siteStatisticsDashboardFilters_v1";
const MAX_SELECTED_SITES = 3;
const FILTER_CHARGING_SITE = "Charging site";


function setCookie(name, value, days = 30) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
        value
    )}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
    const key = encodeURIComponent(name) + "=";
    const parts = document.cookie.split("; ");
    for (const part of parts) {
        if (part.startsWith(key)) return decodeURIComponent(part.slice(key.length));
    }
    return null;
}

export default function SiteStatisticsFilter({ onChangeSitePublicIds, onLoadingChange, startDate, endDate }) {
    const { t } = useTranslation();
    const saved = useMemo(() => {
        try {
            const raw = getCookie(COOKIE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }, []);

    const [sites, setSites] = useState([]);
    const [isLoadingSites, setIsLoadingSites] = useState(true);
    const [sitesError, setSitesError] = useState(null);
    const { getAccessToken } = useAuth();

    // 1) Hämta alla chargingSites från API
    useEffect(() => {
        let cancelled = false;

        const handleGetSites = async () => {
            try {
                setIsLoadingSites(true);
                onLoadingChange?.(true);

                const accessToken = await getAccessToken();
                const newSites = await getAllChargingSites(accessToken);
                const sortedSites = newSites.sort((a, b) =>
                    a.name.localeCompare(b.name, 'sv', { sensitivity: 'base' })
                );

                if (!cancelled) {
                    setSites(sortedSites);
                    onLoadingChange?.(false);

                }

            } catch (error) {
                console.error("Failed to fetch charging sites:", error);
                if (!cancelled) {
                    setSitesError(error?.message ?? t("siteStats.failedFetchSites"));
                    toast.error(error?.message ?? t("siteStats.failedFetchSites"));
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingSites(false);
                    onLoadingChange?.(false);
                }
            }
        };

        handleGetSites();

        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [getAccessToken]);

    const rawDimension = useMemo(
        () => [
            {
                label: FILTER_CHARGING_SITE,
                options: sites.map((s) => s.name),
                selected: [],
                multiSelect: true,
                searchable: true,
                isActive: false,
                maxSelectedOptions: MAX_SELECTED_SITES,
                maxSelectedOptionsMessage: t("siteStats.maxSites", { count: MAX_SELECTED_SITES }),
            },
        ],
        [sites, t]
    );

    const [dimension, setDimension] = useState(rawDimension);

    useEffect(() => {
        setDimension((prev) => {
            const prevSelected =
                prev.find((d) => d.label === FILTER_CHARGING_SITE)?.selected ?? [];

            return prev.map((d) =>
                d.label === FILTER_CHARGING_SITE
                    ? { ...d, options: sites.map((s) => s.name), selected: prevSelected.slice(0, MAX_SELECTED_SITES), maxSelectedOptionsMessage: t("siteStats.maxSites", { count: MAX_SELECTED_SITES }) }
                    : d
            );
        });
    }, [sites, t]);

    useEffect(() => {
        if (isLoadingSites) return;

        const savedIds = saved?.sites ?? [];
        const savedNames = savedIds
            .map((id) => sites.find((s) => s.chargingSitePublicId === id)?.name)
            .filter(Boolean);

        setDimension((prev) =>
            prev.map((d) =>
                d.label === FILTER_CHARGING_SITE ? { ...d, selected: savedNames.slice(0, MAX_SELECTED_SITES) } : d
            )
        );
    }, [saved, sites, isLoadingSites]);

    const chargingSitePublicId = useMemo(() => {
        const selectedNames =
            (dimension.find((d) => d.label === FILTER_CHARGING_SITE)?.selected ?? []).slice(0, MAX_SELECTED_SITES);

        return selectedNames
            .map((name) => sites.find((s) => s.name === name)?.chargingSitePublicId)
            .filter(Boolean);
    }, [dimension, sites]);

    useEffect(() => {
        onChangeSitePublicIds(chargingSitePublicId);

        setCookie(
            COOKIE_KEY,
            JSON.stringify({
                sites: chargingSitePublicId
            }),
            30
        );
    }, [chargingSitePublicId, onChangeSitePublicIds, startDate, endDate]);


    if (sitesError) {
        return (
            <div className="mt-4 text-sm text-red-600">
                {t("siteStats.couldNotLoadSites")}
            </div>
        );
    }

    return (
        <div className="mt-4">
            <FilterDimension
                dimension={dimension}
                setdimension={(d) => d && setDimension(d)}
                index={0}
                allowDeleteDimension={false}
                deleteDimension={() => { }}
            />
        </div>
    );
}
